/**
 * Surface the build fingerprint baked into runtimeConfig at build time.
 *
 * - Logs to console on boot so it's visible in DevTools without any extra UI.
 * - Stamps `<meta name="x-build-sha" content="...">` so curl/HEAD checks and
 *   browser extensions can read it.
 * - Exposes `window.__APP_BUILD__` for quick eyeball checks ("is the fix live?").
 *
 * Populated by `task_build_front` (script:`build_front <env>`) which writes
 * NUXT_PUBLIC_APP_BUILD_{SHA,BRANCH,TIME,ENV} into frontend/.env, mapping to
 * runtimeConfig.public.app.{buildSha,buildBranch,buildTime,buildEnv}.
 * On localhost the values are empty strings; we expose them anyway so the
 * absence is obvious (don't silently hide unbuilt state).
 */

interface BuildInfo {
  sha: string
  srcSha: string
  distSha: string
  branch: string
  time: string
  env: string
  dirty: boolean
}

declare global {
  interface Window {
    __APP_BUILD__?: BuildInfo
  }
}

export default defineNuxtPlugin(() => {
  const cfg = useRuntimeConfig().public.app as {
    buildSha?: string
    buildSrcSha?: string
    buildDistSha?: string
    buildBranch?: string
    buildTime?: string
    buildEnv?: string
    buildDirty?: string
  } | undefined

  const info: BuildInfo = {
    sha: cfg?.buildSha ?? '',
    srcSha: cfg?.buildSrcSha ?? '',
    distSha: cfg?.buildDistSha ?? '',
    branch: cfg?.buildBranch ?? '',
    time: cfg?.buildTime ?? '',
    env: cfg?.buildEnv ?? '',
    dirty: cfg?.buildDirty === 'true'
  }

  window.__APP_BUILD__ = info

  const meta = document.createElement('meta')
  meta.setAttribute('name', 'x-build-sha')
  meta.setAttribute('content', info.sha + (info.dirty ? '+dirty' : ''))
  document.head.appendChild(meta)

  if (info.sha) {
    const dirtyTag = info.dirty ? ' DIRTY-WORKING-TREE' : ''
    const srcTag = info.srcSha ? ` src=${info.srcSha}` : ''
    const distTag = info.distSha ? ` dist=${info.distSha}` : ''
    console.info(
      `%c[build] ${info.env || '(no env)'} sha=${info.sha.slice(0, 7)}${srcTag}${distTag}${dirtyTag} branch=${info.branch} time=${info.time}`,
      info.dirty ? 'color: #c08000' : 'color: #888'
    )
  } else {
    console.info('[build] runtime config has no build fingerprint (running unbuilt or in dev)')
  }
})
